import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Spin, Alert, Card, Row, Col, Typography } from 'antd';
import { EditOutlined, StopOutlined } from '@ant-design/icons';
import moment from 'moment';
import InvalidTripModal from './InvalidTripModal';
import { ACL } from 'components/common';
import { useAuth } from 'hooks';

const { Text } = Typography;

const labelStyle = { color: '#999', fontWeight: 400, fontSize: 14 };
const valueStyle = { color: '#000', fontWeight: 500, fontSize: 16 };

// V1: dates come as UTC, need to convert to IST (+5:30)
const formatDateShortV1 = (dateStr) => {
	if (!dateStr) return '';
	return moment(dateStr).add(5, 'hours').add(30, 'minutes').format('DD-MM-YYYY');
};

const formatTimeShortV1 = (dateStr) => {
	if (!dateStr) return '';
	return moment(dateStr).add(5, 'hours').add(30, 'minutes').format('h:mm A');
};

// V2: dates already sent in IST by backend — no offset needed
const formatDateShortV2 = (dateStr) => {
	if (!dateStr) return '';
	return moment(dateStr).format('DD-MM-YYYY');
};

const formatTimeShortV2 = (dateStr) => {
	if (!dateStr) return '';
	return moment(dateStr).format('h:mm A');
};

const normalizeReportingDateTime = (value) => {
	if (typeof value !== 'string') return value;
	const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2}:\d{2})$/);
	if (!match) return value;
	const [, year, part2, part3, time] = match;
	const monthCandidate = Number(part2);
	if (monthCandidate >= 1 && monthCandidate <= 12) return value;
	return `${year}-${part3}-${part2}T${time}`;
};

function TripOverviewTab({
	tripDetails,
	loading,
	error,
	// searchValue,
	// setSearchValue,
	// handleSearch,
	tripId,
	navigate,
	isV2 = false,
}) {
	const formatDateShort = isV2 ? formatDateShortV2 : formatDateShortV1;
	const formatTimeShort = isV2 ? formatTimeShortV2 : formatTimeShortV1;
	const invoiceNumber = tripDetails?.invoiceNumber?.slice(0, 10) || '-';
	const isVendorBilled = tripDetails?.is_vendor_billed === true;
	const invoiceDate = tripDetails?.invoiceDate?.slice(0, 10) || '-';
	const [isInvalidTripModalOpen, setIsInvalidTripModalOpen] = useState(false);
	const [isTripValid, setIsTripValid] = useState(tripDetails?.isValid);
	const { user_type } = useAuth();

	useEffect(() => {
		setIsTripValid(tripDetails?.isValid);
	}, [tripDetails?.isValid]);
	// Define the field mappings for the first row
	const firstRowFields = [
		{ label: 'Client', field: 'client' },
		{ label: 'Project', field: 'project' },
		{ label: 'Vendor', field: 'vendor' },
		{ label: 'Reporting Date & Time', field: 'reportingDateTime', isDateTime: true },
		{ label: 'Start Date & Time', field: 'startDateTime', isDateTime: true },
		{ label: 'End Date & Time', field: 'endDateTime', isDateTime: true },
		{ label: 'Source Location*', field: 'sourceLocation' },
		{ label: 'Destination Location*', field: 'destinationLocation' },
		{ label: 'Trip Type', field: 'tripType' },
	];

	// Define the field mappings for the second row
	const secondRowFields = [
		{ label: 'Vendor Type*', field: 'vendorType' },
		{ label: 'Vendor Name*', field: 'vendor' },
		{ label: 'City', field: 'city' },
		{ label: 'Project Name*', field: 'projectName' },
		{ label: 'Client Rate*', field: 'clientRate' },
		{ label: 'Vendor Rate*', field: 'vendorRate' },
		{ label: 'Driver Number*', field: 'driverNumber' },
		{ label: 'Driver Name*', field: 'driverName' },
		{ label: 'Vehicle Type*', field: 'vehicleType' },
		{ label: 'Vehicle Number*', field: 'vehicleNumber' },
		...(tripDetails?.interCity === false ? [{ label: 'Start KM', field: 'startKm' }] : []),
		...(tripDetails?.interCity === false ? [{ label: 'End KM', field: 'endKm' }] : []),
		{ label: 'Adhoc Request ID', field: 'adhocRequestId' },
		{ label: 'Advance Amount', field: 'advanceAmount' },
		{ label: 'Trip Status', field: 'tripStatusName' },
	];

	const renderFieldValue = (field, isDateTime = false) => {
		const rawValue = tripDetails?.[field];

		// Mask sensitive rates for supervisors
		if ((field === 'clientRate' || field === 'vendorRate') && user_type === 'supervisor') {
			return '****';
		}

		if (isDateTime && rawValue) {
			const value =
				field === 'reportingDateTime' ? normalizeReportingDateTime(rawValue) : rawValue;
			const dateTimeString = `${formatDateShort(value)} ${formatTimeShort(value)}`;

			// Special handling for End Date & Time to include runtime
			if (field === 'endDateTime' && tripDetails?.runTime) {
				return `${dateTimeString} (${tripDetails.runTime})`;
			}

			return dateTimeString;
		}

		return rawValue || '-';
	};

	const handleModalClose = () => {
		setIsInvalidTripModalOpen(false);
		window.location.reload();
	};

	return (
		<>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: 24,
					padding: '0 0 24px 0',
					background: 'transparent',
					borderRadius: 8,
					minHeight: 56,
					borderBottom: '1px solid #eaeaea',
				}}
			>
				{/* <div style={{ display: 'flex', alignItems: 'center', minWidth: 260 }}>
					<Input
						placeholder="Search"
						value={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						style={{
							width: 180,
							borderRadius: 6,
							border: '1px solid #d9d9d9',
							marginRight: 0,
						}}
					/>
					<Button
						type="primary"
						onClick={handleSearch}
						style={{ borderRadius: 0, marginLeft: 0 }}
					>
						Search
					</Button>
				</div> */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						flex: 1,
						justifyContent: 'center',
						gap: 48,
					}}
				>
					<div style={{ textAlign: 'center', minWidth: 120 }}>
						<div style={{ fontSize: 14, color: '#888', marginBottom: 2 }}>
							Invoice Number
						</div>
						<div style={{ fontWeight: 400, fontSize: 16, color: '#222' }}>
							{invoiceNumber}
						</div>
					</div>
					<div
						style={{
							height: 32,
							borderLeft: '2px dashed #1890ff',
							margin: '0 24px',
						}}
					/>
					<div style={{ textAlign: 'center', minWidth: 120 }}>
						<div style={{ fontSize: 14, color: '#888', marginBottom: 2 }}>
							Invoice Date
						</div>
						<div style={{ fontWeight: 400, fontSize: 16, color: '#222' }}>
							{invoiceDate}
						</div>
					</div>
				</div>
				<div style={{ minWidth: 120, display: 'flex', justifyContent: 'flex-end' }}>
					<ACL action="update" module="trip">
						{/* eslint-disable-next-line */}
						{tripDetails?.trip_status !== 3 ? (
							<Button
								type="primary"
								onClick={() => {
									if (tripDetails?.interCity === false) {
										navigate(`/trips/${tripId}/add-intracity`);
									} else {
										navigate(`/trips/${tripId}/add-intercity`);
									}
								}}
								style={{
									minWidth: 90,
									fontWeight: 500,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
									<EditOutlined style={{ fontSize: 16, marginRight: 4 }} />
									EDIT
								</span>
							</Button>
						) : (
							<ACL action="search" module="invalidate_trip">
								<Button
									type="primary"
									disabled={isVendorBilled || !isTripValid}
									style={{ backgroundColor: '#FF3B30', borderColor: '#FF3B30' }}
									onClick={() => {
										setIsInvalidTripModalOpen(true);
									}}
								>
									<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
										<StopOutlined style={{ fontSize: 16, marginRight: 4 }} />
										Mark Invalid
									</span>
								</Button>
							</ACL>
						)}
					</ACL>
					<InvalidTripModal
						open={isInvalidTripModalOpen}
						onClose={handleModalClose}
						tripId={tripId}
						onSuccess={() => setIsTripValid(false)}
					/>
				</div>
			</div>
			{loading && <Spin tip="Loading trip details..." />}
			{error && <Alert type="error" message={error} />}
			{!loading && !error && tripDetails && (
				<Card
					style={{ marginTop: 16, borderRadius: 8 }}
					bodyStyle={{ padding: 24, position: 'relative' }}
				>
					<Row gutter={[32, 16]}>
						{firstRowFields.map(({ label, field, isDateTime }) => (
							<Col span={8} key={label}>
								<Text style={labelStyle}>{label}</Text>
								<div style={valueStyle}>{renderFieldValue(field, isDateTime)}</div>
							</Col>
						))}
					</Row>
					<div style={{ borderTop: '1px solid #eee', margin: '32px 0 16px 0' }} />
					<Row gutter={[32, 16]}>
						{secondRowFields.map(({ label, field, isDateTime }) => (
							<Col span={8} key={label}>
								<Text style={labelStyle}>{label}</Text>
								<div style={valueStyle}>{renderFieldValue(field, isDateTime)}</div>
							</Col>
						))}
					</Row>
				</Card>
			)}
		</>
	);
}

TripOverviewTab.propTypes = {
	tripDetails: PropTypes.shape({
		invoiceNumber: PropTypes.string,
		invoiceDate: PropTypes.string,
		interCity: PropTypes.bool,
		isValid: PropTypes.bool,
		is_vendor_billed: PropTypes.bool,
		reportingDateTime: PropTypes.string,
		endDateTime: PropTypes.string,
		startDateTime: PropTypes.string,
		client: PropTypes.string,
		project: PropTypes.string,
		vendor: PropTypes.string,
		runTime: PropTypes.string,
		sourceLocation: PropTypes.string,
		destinationLocation: PropTypes.string,
		tripType: PropTypes.string,
		vendorType: PropTypes.string,
		city: PropTypes.string,
		projectName: PropTypes.string,
		clientRate: PropTypes.string,
		vendorRate: PropTypes.string,
		driverNumber: PropTypes.string,
		driverName: PropTypes.string,
		vehicleType: PropTypes.string,
		vehicleNumber: PropTypes.string,
		trip_status: PropTypes.number,
		adhocRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		advanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		tripStatusName: PropTypes.string,
	}),
	loading: PropTypes.bool,
	error: PropTypes.string,
	// searchValue: PropTypes.string,
	// setSearchValue: PropTypes.func,
	// handleSearch: PropTypes.func,
	tripId: PropTypes.string,
	navigate: PropTypes.func,
	isV2: PropTypes.bool,
};

export default TripOverviewTab;
